import React, { useMemo, useState } from "react";
import DataTableComponent from "../common/DataTableComponent";
import { getTableHeader } from "../systemadministration/CampusManagement/TableComponents";
import { tokens } from "../../public/tokens";
import { tableCustomStyles, tableHeaderStyle } from "./careerLibrary.styles";
import { applicableFilterOptions } from "./careerLibraryUtils";

const { globals } = tokens;

const defaultTableState = {
    page: 1,
    perPage: 10,
    sortKey: "",
    sortOrder: "asc",
};
const emptyFilterState = { arr: [], checkObj: {} };

/**
 * The design's linked-items table, built on the shared DataTableComponent so it
 * inherits the app's search, filters, reset, export and pagination chrome. Both
 * linked sets are small, so filtering and paging stay client-side.
 * @param {object} props rows, columns, searchText, filters, exportRow, link button and className
 */
function LinkedItemsTable({
    rows,
    columns,
    searchText,
    filters = [],
    exportFileName = "linked_items",
    exportRow,
    linkButton,
    className = "",
}) {
    const [tableState, setTableState] = useState(defaultTableState);
    const [search, setSearch] = useState("");
    const [filterValues, setFilterValues] = useState({});

    const filteredRows = useMemo(() => {
        const searchTerm = String(search || "")
            .trim()
            .toLowerCase();
        return rows.filter((row) => {
            if (
                searchTerm &&
                !searchText(row).toLowerCase().includes(searchTerm)
            ) {
                return false;
            }
            return filters.every((filter) => {
                const selected = (filterValues[filter.key] || emptyFilterState)
                    .arr;
                if (!selected.length) {
                    return true;
                }
                return selected.includes(filter.readValue(row));
            });
        });
    }, [rows, search, filterValues, filters, searchText]);

    const tableColumns = useMemo(
        () =>
            columns.map((column) => ({
                ...column,
                name: getTableHeader(
                    column.name,
                    tableState.sortKey === column.selector,
                    tableHeaderStyle,
                ),
            })),
        [columns, tableState.sortKey],
    );

    return (
        <div className={`career-links-table ${className}`.trim()}>
            <DataTableComponent
                data={filteredRows}
                columns={tableColumns}
                state={tableState}
                setState={setTableState}
                setSearch={setSearch}
                totalRows={filteredRows.length}
                exportFileName={exportFileName}
                exportFunction={(fileType) =>
                    exportRow && exportRow(fileType, filteredRows)
                }
                btnStyleOverride={{ borderRadius: globals["border-radius"] }}
                containerStyle={{
                    borderRadius: globals["border-radius"],
                    padding: 0,
                }}
                filterBtnSpacing="10px"
                tableButton={linkButton ? [linkButton] : []}
                filters={filters.map((filter) => ({
                    filterName: filter.label,
                    uniqueId: `linked-${filter.key}`,
                    optionArr: applicableFilterOptions(
                        rows,
                        filters,
                        filterValues,
                        filter,
                    ),
                    renderLabelFunction: filter.renderLabelFunction,
                    state: filterValues[filter.key] || emptyFilterState,
                    setState: (next) => {
                        const current =
                            filterValues[filter.key] || emptyFilterState;
                        const resolved =
                            typeof next === "function" ? next(current) : next;
                        setFilterValues((currentValues) => ({
                            ...currentValues,
                            [filter.key]: resolved || emptyFilterState,
                        }));
                    },
                    isOptionReversed: false,
                }))}
                otherTableProps={{
                    highlightOnHover: false,
                    responsive: true,
                    customStyles: tableCustomStyles,
                }}
            />
        </div>
    );
}

export default LinkedItemsTable;
